import { ChevronLeft, ChevronRight } from 'lucide-react'
import { dayKey } from '@/lib/time'
import { TONE_CLASS, type Tone } from '@/lib/status'
import { cn } from '@/lib/utils'
import { IconButton } from './Button'
import { PlatformMark } from './PlatformMark'
import { StatusDot } from './StatusPill'
import type { Source } from './SourceTag'

export const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토']

/** One thing on one day of the grid. */
export interface CalendarEntry {
  key: string
  /** "2026-10-15", in the browser's zone. */
  day: string
  /** "14:30"; '' for an all-day marker, which sorts first. */
  time: string
  /** What fits in a day cell. */
  label: string
  /** The whole line, for the tooltip. */
  title: string
  tone: Tone
  /** Which programme a post belongs to; markers have none. */
  source?: Source
  /** Where a post goes, drawn as its mark first: the owner's name alone
   *  can't tell a desk post from a campaign post of the same product. */
  platform?: string
  dim?: boolean
}

/** The month as rows of seven, Sunday first, padded with null outside it. */
export function monthCells(year: number, month: number): (string | null)[] {
  const cells: (string | null)[] = []
  for (let i = 0; i < new Date(year, month, 1).getDay(); i++) cells.push(null)
  const days = new Date(year, month + 1, 0).getDate()
  for (let d = 1; d <= days; d++) cells.push(dayKey(new Date(year, month, d)))
  while (cells.length % 7) cells.push(null)
  return cells
}

export function parseDay(key: string): Date {
  const [y, m, d] = key.split('-').map(Number)
  return new Date(y, m - 1, d)
}

/** "10월 15일 (목)" — a day's accessible name and its panel heading. */
export function describeDay(key: string): string {
  const t = parseDay(key)
  return `${t.getMonth() + 1}월 ${t.getDate()}일 (${WEEKDAYS[t.getDay()]})`
}

/** The seven days, Sunday first, of the week that holds `key`. */
export function weekDays(key: string): string[] {
  const d = parseDay(key)
  const start = new Date(d.getFullYear(), d.getMonth(), d.getDate() - d.getDay())
  return Array.from({ length: 7 }, (_, i) => dayKey(new Date(start.getFullYear(), start.getMonth(), start.getDate() + i)))
}

const SOURCE_EDGE: Record<Source, string> = {
  redeem: 'border-l-2 border-source-redeem',
  campaign: 'border-l-2 border-source-campaign',
}

export function EntryChip({ entry, past }: { entry: CalendarEntry; past?: boolean }) {
  const c = TONE_CLASS[entry.tone]
  return (
    <span
      className={cn(
        'block truncate rounded-[4px] px-1 text-micro leading-5',
        c.soft,
        c.text,
        entry.source && SOURCE_EDGE[entry.source],
        (entry.dim || (past && entry.time)) && 'opacity-70',
      )}
      title={entry.title}
    >
      {entry.platform && (
        <PlatformMark platform={entry.platform} className="mr-1 h-[14px] min-w-[14px] rounded-[3px] px-[2px] align-[-2px] text-[8px]" />
      )}
      {entry.time && <span className="tabular-nums">{entry.time} </span>}
      {entry.label}
    </span>
  )
}

export interface MonthCalendarProps {
  year: number
  /** 0-based, as Date counts it. */
  month: number
  today: string
  selected: string
  onSelect: (day: string) => void
  entries: Map<string, CalendarEntry[]>
  /** Entries a cell shows before "+N". */
  perDay?: number
  className?: string
}

/** The month grid. A day is a button; it shows up to three entries and how
 *  many more there are. */
export function MonthCalendar({ year, month, today, selected, onSelect, entries, perDay = 3, className }: MonthCalendarProps) {
  return (
    <div className={cn('overflow-hidden rounded-lg border border-border bg-card', className)}>
      <div className="grid grid-cols-7 border-b border-border bg-muted/50 text-center text-meta text-muted-foreground">
        {WEEKDAYS.map((w, i) => (
          <div key={w} className={cn('py-1.5', i === 0 && 'text-status-failed', i === 6 && 'text-status-scheduled')}>
            {w}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {monthCells(year, month).map((day, i) => {
          if (!day) return <div key={`pad-${i}`} className="min-h-[6.5rem] border-b border-r border-border bg-muted/20" />
          const list = entries.get(day) ?? []
          const past = day < today
          return (
            <button
              key={day}
              type="button"
              onClick={() => onSelect(day)}
              aria-label={describeDay(day)}
              aria-pressed={day === selected}
              className={cn(
                'flex min-h-[6.5rem] min-w-0 flex-col gap-1 border-b border-r border-border p-1.5 text-left align-top transition-colors hover:bg-muted/40',
                day === selected && 'bg-accent/5 ring-1 ring-inset ring-accent/50',
              )}
            >
              <span
                className={cn(
                  'inline-flex h-5 w-5 items-center justify-center rounded-full text-meta tabular-nums',
                  day === today
                    ? 'bg-primary font-semibold text-primary-foreground'
                    : past
                      ? 'text-muted-foreground/60'
                      : 'text-foreground',
                )}
              >
                {Number(day.slice(8))}
              </span>
              {list.slice(0, perDay).map((e) => (
                <EntryChip key={e.key} entry={e} past={past} />
              ))}
              {list.length > perDay && (
                <span className="px-1 text-micro text-muted-foreground">+{list.length - perDay}</span>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}

export interface WeekStripProps {
  /** The seven days shown, Sunday first. */
  days: string[]
  today: string
  selected: string
  onSelect: (day: string) => void
  entries: Map<string, CalendarEntry[]>
  onPrev: () => void
  onNext: () => void
  className?: string
}

/** The phone's calendar: one week as seven buttons, a dot per entry (three at
 *  most), with the week before and after a tap away. */
export function WeekStrip({ days, today, selected, onSelect, entries, onPrev, onNext, className }: WeekStripProps) {
  return (
    <div className={cn('flex items-stretch gap-1 rounded-lg border border-border bg-card p-1', className)}>
      <IconButton label="이전 주" icon={ChevronLeft} size="sm" onClick={onPrev} className="self-center" />
      <div className="grid min-w-0 flex-1 grid-cols-7 gap-0.5">
        {days.map((day, i) => {
          const list = entries.get(day) ?? []
          const on = day === selected
          return (
            <button
              key={day}
              type="button"
              onClick={() => onSelect(day)}
              aria-label={describeDay(day)}
              aria-pressed={on}
              className={cn(
                'flex min-w-0 flex-col items-center gap-1 rounded-md py-1.5 transition-colors',
                on ? 'bg-primary text-primary-foreground' : 'hover:bg-muted',
              )}
            >
              <span
                className={cn(
                  'text-micro',
                  !on && (i === 0 ? 'text-status-failed' : i === 6 ? 'text-status-scheduled' : 'text-muted-foreground'),
                )}
              >
                {WEEKDAYS[i]}
              </span>
              <span
                className={cn(
                  'text-body font-semibold tabular-nums',
                  !on && day === today && 'text-accent',
                  !on && day < today && 'text-muted-foreground',
                )}
              >
                {Number(day.slice(8))}
              </span>
              <span className="flex h-1.5 items-center gap-0.5" aria-hidden>
                {list.slice(0, 3).map((e) => (
                  <StatusDot key={e.key} tone={e.tone} className={on ? 'ring-1 ring-primary-foreground/60' : undefined} />
                ))}
              </span>
              <span className="sr-only">{list.length}건</span>
            </button>
          )
        })}
      </div>
      <IconButton label="다음 주" icon={ChevronRight} size="sm" onClick={onNext} className="self-center" />
    </div>
  )
}
